

















































































import { Component, Vue } from 'vue-property-decorator';
import { mdbBtn, mdbTooltip, mdbRow, mdbCol, mdbIcon, mdbAlert, mdbSwitch } from '@folky-tools/mdbvue';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import { Reference } from '@/models/references/reference';
import { ReferenceSku } from '@/models/references/reference-sku';
import { Attribute } from '@/models/references-classification/attribute';
import { referencesService, referencesClassificationService, usersService, notificationsService } from '@/services';
import { Guid } from 'guid-typescript';

@Component({
    components: {
        mdbBtn,
        mdbTooltip,
        mdbRow,
        mdbCol,
        mdbIcon,
        mdbAlert,
        mdbSwitch,
        EditReferenceSku: () => import('@/components/references/EditReferenceSku.vue'),
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class EditReferenceSkusView extends Vue {
    private reference: Reference = new Reference();
    private referenceCopy: Reference = new Reference();
    private skuAttributes: Attribute[] = [];
    private referenceSkuHasChanged: boolean = false;
    private applyingChanges: boolean = false;
    private showPricePerUOM: boolean = false;

    private pageContentLoaded: boolean = false;

    private async mounted(): Promise<void> {
        this.$root.$emit('change-active-page', 'views.references.editReferenceSkusView.title');

        await this.init();

        this.reference.skus.forEach(sku => {
            if (sku.unitPrice !== undefined) {
                this.showPricePerUOM = true;
            }
        });

        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.Products,
                scope: UserActionScopeType.Skus,
                entityId: +this.$route.params.id
            })
        );

        setTimeout(() => {
            this.pageContentLoaded = true;
        }, 10);
    }

    private async init(): Promise<void> {
        this.reference = await referencesService.getReference(+this.$route.params.id);
        this.referenceCopy = this.reference.clone();
        this.skuAttributes = await referencesClassificationService.getAttributes(this.reference.businessId!);
    }

    private newSku(): void {
        const newSku = this.reference.skus.length === 0 ? new ReferenceSku({ isPrincipal: true }) : this.reference.skus[this.reference.skus.length - 1].clone();
        if (this.reference.skus.length > 0) {
            newSku.id = Math.min(...this.reference.skus.map(sku => sku.id)) - 1;
            if (newSku.id > 0) {
                newSku.id = 0;
            }
            newSku.isPrincipal = false;
            newSku.code = undefined;
            newSku.position = this.reference.skus[this.reference.skus.length - 1].position + 1;
        }
        this.reference.skus.push(newSku);

        this.onSkuChanged();
    }

    private async deleteSku(id: number): Promise<void> {
        this.reference.skus = this.reference.skus.filter(sku => sku.id !== id);
        if (this.reference.skus.length > 0) {
            this.reference.skus[0].isPrincipal = true;
        }

        for (const sku of this.$refs.skus as any) {
            if (sku.sku.id !== id && sku.hasChanges()) {
                this.referenceSkuHasChanged = true;
                return;
            }
        }
        this.referenceSkuHasChanged = this.reference.skus.length !== this.referenceCopy.skus.length;
    }

    private async onSkuMoved(skuIndex: number, direction: string): Promise<void> {
        const sku1: ReferenceSku = this.reference.skus[skuIndex].clone();
        const sku2: ReferenceSku = this.reference.skus[direction === 'up' ? skuIndex - 1 : skuIndex + 1].clone();

        sku1.position = sku2.position;
        sku2.position = this.reference.skus[skuIndex].position;

        this.reference.skus[skuIndex] = sku2.clone();
        this.reference.skus[direction === 'up' ? skuIndex - 1 : skuIndex + 1] = sku1.clone();
        this.reference.skus.forEach(sku => {
            sku.isPrincipal = false;
        });
        this.reference.skus[0].isPrincipal = true;
        this.reference = this.reference.clone();

        this.onSkuChanged();
    }

    private onSkuDragEnd(data: any): void {
        let skuPosition: number = 1;
        this.reference.skus.forEach(sku => {
            sku.isPrincipal = false;
            sku.position = skuPosition++;
        });
        this.reference.skus[0].isPrincipal = true;

        this.onSkuChanged();
    }

    private onSkuChanged(): void {
        this.$nextTick(() => {
            for (const sku of this.$refs.skus as any) {
                if (sku.hasChanges()) {
                    this.referenceSkuHasChanged = true;
                    return;
                }
            }
            this.referenceSkuHasChanged = this.reference.skus.length !== this.referenceCopy.skus.length;
        });
    }

    private generateSkus(): void {
        this.$router.push(`/products/${this.reference.id}/generate-skus/${this.reference.category.id}?referenceTitle=${this.reference.title}`);
    }

    private revertSkuChanges(): void {
        this.$router.push(`/products/${this.reference.id}/edit-skus?referenceTitle=${this.reference.title}&refresh=${Guid.create().toString()}`);
    }

    private async applySkuChanges(): Promise<void> {
        this.applyingChanges = true;

        try {
            for (const sku of this.$refs.skus as any) {
                if (!sku.atLeastOneDataFilledIn()) {
                    this.reference.skus = this.reference.skus.filter(s => s.id !== sku.sku.id);
                }
            }

            if (this.referenceSkuHasChanged && (await referencesService.updateReferenceSkus(this.reference.id, this.reference.skus))) {
                notificationsService.showToastSuccessNotification('views.references.editReferenceSkusView.notifications.updateReferenceSkusSuccess');

                this.referenceSkuHasChanged = false;

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Products,
                        scope: UserActionScopeType.Skus,
                        entityId: this.reference.id
                    })
                );

                this.$router.push(`/products/${this.reference.id}`);

                return;
            }

            notificationsService.showToastErrorNotification();
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.applyingChanges = false;
        }
    }
}
